import { Skeleton, Space } from '@dnb/eufemia';
import { type JSX, useMemo } from 'react';

import { HeroContent } from '@/components/HeroPage/HeroContent';
import { HeroContentMenu } from '@/components/HeroPage/HeroContentMenu';
import HeroMenu from '@/components/HeroPage/HeroMenu';
import { NavigationMenu } from '@/components/NavigationMenu';
import { useBackend } from '@/hooks/useBackend';
import { capitalizeFirstLetterOfString } from '@/utils/textUtils';

import EventResourceNotFoundError from '../EventResourceNotFoundError';
import SchemaView from './SchemaView';

import style from './index.module.css';

export interface Props {
  environmentSlug: string;
  eventSlug: string;
  releaseVersion: string;
  topChild: JSX.Element;
}

export default function SchemasView({ environmentSlug, eventSlug, releaseVersion, topChild }: Props) {
  const { data: schemas, isLoading: isLoadingSchemas } = useBackend(
    `/events/{eventSlug}/environments/{environmentSlug}/releases/{releaseVersion}/schemas`,
    {
      params: {
        path: {
          eventSlug,
          environmentSlug,
          releaseVersion,
        },
      },
    },
  );

  const navigationItems = useMemo(() => {
    if (!schemas) return undefined;
    return schemas.map((schema) => ({
      displayName: `${capitalizeFirstLetterOfString(schema.type)} Schema`,
      slug: `${schema.type}-schema`,
    }));
  }, [schemas]);

  return (
    <>
      <HeroMenu noPadding>
        <Space bottom="small" left="large" right="large" top="large">
          {topChild}
        </Space>
        {navigationItems && <NavigationMenu items={navigationItems} />}
      </HeroMenu>
      <HeroContent>
        <HeroContentMenu>{topChild}</HeroContentMenu>
        <Skeleton show={isLoadingSchemas}>
          {schemas && schemas.length > 0 ? (
            <div className={style['SchemaViewList']}>
              {schemas.map((schema) => (
                <SchemaView eventSlug={eventSlug} key={schema.type} schema={schema} />
              ))}
            </div>
          ) : (
            <EventResourceNotFoundError type="schema" />
          )}
        </Skeleton>
      </HeroContent>
    </>
  );
}
